/**
 * WlPlanMatrix — Matriz de Planos × Canais/Provedores no painel White Label.
 *
 * Leiaute: tabela pivot com células-chip.
 *   - Linhas: canais (ou provedores na aba "Provedores")
 *   - Colunas: planos (cabeçalho sticky horizontal, primeira coluna sticky vertical)
 *   - Célula clicável abre Popover com switches/inputs e grava via RPC SECURITY DEFINER
 *
 * Toda leitura/escrita passa por `useWlPlanMatrix` (RPCs wl_*).
 * Permissões via `useWlPermissions('wl.plan_matrix')` — observer fica read-only.
 */
import { useMemo, useState } from "react";
import {
  LayoutGrid,
  MessageSquare,
  Plug,
  Search,
  Check,
  X,
  Plus,
  Star,
  Power,
} from "lucide-react";
import { useWlPermissions } from "@/contexts/WlPermissionsContext";
import {
  useWlPlanMatrix,
  useWlPlanMatrixMutations,
  type WlMatrixPlan,
  type WlMatrixChannelType,
  type WlMatrixProvider,
  type WlMatrixPlanChannel,
  type WlMatrixPlanProvider,
} from "@/hooks/useWlPlanMatrix";
import { showToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  ListPageContent,
} from "@/components/layout/list-page";
import { applyCurrencyMask, parseCurrency } from "@/lib/input-masks";

// ============= Helpers =============

// Converte valor em centavos (integer no banco) para BRL formatado.
const fmtCents = (cents: number | null | undefined) => {
  if (cents == null) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(cents) / 100,
  );
};

// Converte BRL digitado pelo usuário para centavos inteiros.
const toCents = (brl: number | null | undefined): number | null => {
  if (brl == null || Number.isNaN(brl)) return null;
  return Math.round(brl * 100);
};

// ============= Célula de Canal =============
interface ChannelCellProps {
  plan: WlMatrixPlan;
  channel: WlMatrixChannelType;
  config?: WlMatrixPlanChannel;
  canEdit: boolean;
  onSave: (input: {
    plan_id: string;
    channel_type_id: string;
    is_available: boolean;
    can_addon: boolean;
    addon_price_monthly: number | null;
    addon_setup_fee: number | null;
  }) => Promise<void>;
}

const ChannelCell = ({ plan, channel, config, canEdit, onSave }: ChannelCellProps) => {
  const isAvailable = config?.is_available ?? false;
  const canAddon = config?.can_addon ?? false;

  const [open, setOpen] = useState(false);
  const [draftAvail, setDraftAvail] = useState(isAvailable);
  const [draftAddon, setDraftAddon] = useState(canAddon);
  const [saving, setSaving] = useState(false);

  // Sincroniza estado do popover ao abrir (caso o cache tenha atualizado)
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraftAvail(isAvailable);
      setDraftAddon(canAddon);
    }
    setOpen(next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Preço mensal e taxa de setup são legado — gestão de valores de add-on
      // acontece em /wl/addons (cada add-on possui product/price próprios no Stripe).
      // Mantemos null aqui para não confundir o operador.
      await onSave({
        plan_id: plan.id,
        channel_type_id: channel.id,
        is_available: draftAvail,
        can_addon: draftAvail ? draftAddon : false,
        addon_price_monthly: null,
        addon_setup_fee: null,
      });
      showToast.success("Configuração salva");
      setOpen(false);
    } catch (e: any) {
      showToast.error(e?.message || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  // Define estado visual do chip
  const state: "off" | "included" | "addon" = !isAvailable
    ? "off"
    : canAddon
    ? "addon"
    : "included";

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={!canEdit}
          className={cn(
            "w-full min-h-[42px] rounded-md border px-2 py-1.5 text-xs transition-all",
            "flex items-center justify-center gap-1 text-center",
            state === "off" && "border-dashed border-muted-foreground/30 bg-muted/30 text-muted-foreground hover:bg-muted/50",
            state === "included" && "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20",
            state === "addon" && "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20",
            canEdit ? "cursor-pointer" : "cursor-not-allowed opacity-70",
          )}
        >
          {state === "off" && <><X className="h-3 w-3" /> Indisponível</>}
          {state === "included" && <><Check className="h-3 w-3" /> Incluso</>}
          {state === "addon" && <><Plus className="h-3 w-3" /> Add-on</>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="center">
        <div className="space-y-3">
          <div className="border-b pb-2">
            <div className="text-sm font-semibold">{channel.display_name}</div>
            <div className="text-xs text-muted-foreground">no plano <strong>{plan.name}</strong></div>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Disponível</Label>
            <Switch checked={draftAvail} onCheckedChange={setDraftAvail} disabled={!canEdit || saving} />
          </div>

          <div className={cn("flex items-center justify-between", !draftAvail && "opacity-50")}>
            <Label className="text-xs">Como add-on</Label>
            <Switch
              checked={draftAddon}
              onCheckedChange={setDraftAddon}
              disabled={!canEdit || saving || !draftAvail}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!canEdit || saving}>
              Salvar
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};



// ============= Célula de Provider =============
interface ProviderCellProps {
  plan: WlMatrixPlan;
  provider: WlMatrixProvider;
  config?: WlMatrixPlanProvider;
  canEdit: boolean;
  onSave: (input: {
    plan_id: string;
    provider_id: string;
    is_available: boolean;
    is_default: boolean;
    shares_plan_pool: boolean;
  }) => Promise<void>;
}

const ProviderCell = ({ plan, provider, config, canEdit, onSave }: ProviderCellProps) => {
  const isAvailable = config?.is_available ?? false;
  const isDefault = config?.is_default ?? false;
  // Elegibilidade à cota do plano: fallback para is_default (retrocompatível)
  const isPoolEligible = config?.shares_plan_pool ?? isDefault;

  const [open, setOpen] = useState(false);
  const [draftAvail, setDraftAvail] = useState(isAvailable);
  const [draftDefault, setDraftDefault] = useState(isDefault);
  const [draftPool, setDraftPool] = useState(isPoolEligible);
  const [saving, setSaving] = useState(false);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraftAvail(isAvailable);
      setDraftDefault(isDefault);
      setDraftPool(isPoolEligible);
    }
    setOpen(next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const nextDefault = draftAvail ? draftDefault : false;
      await onSave({
        plan_id: plan.id,
        provider_id: provider.id,
        is_available: draftAvail,
        is_default: nextDefault,
        // Provider padrão é sempre elegível; indisponível nunca é
        shares_plan_pool: !draftAvail ? false : nextDefault ? true : draftPool,
      });
      showToast.success("Configuração salva");
      setOpen(false);
    } catch (e: any) {
      showToast.error(e?.message || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const state: "off" | "on" | "pool" | "default" = !isAvailable
    ? "off"
    : isDefault
    ? "default"
    : isPoolEligible
    ? "pool"
    : "on";

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={!canEdit}
          className={cn(
            "w-full min-h-[42px] rounded-md border px-2 py-1.5 text-xs transition-all",
            "flex items-center justify-center gap-1 text-center",
            state === "off" && "border-dashed border-muted-foreground/30 bg-muted/30 text-muted-foreground hover:bg-muted/50",
            state === "on" && "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20",
            state === "pool" && "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400 hover:bg-sky-500/20",
            state === "default" && "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20",
            canEdit ? "cursor-pointer" : "cursor-not-allowed opacity-70",
          )}
        >
          {state === "off" && <><X className="h-3 w-3" /> Indisponível</>}
          {state === "on" && <><Check className="h-3 w-3" /> Habilitado</>}
          {state === "pool" && <><Star className="h-3 w-3" /> Cota do plano</>}
          {state === "default" && <><Star className="h-3 w-3 fill-current" /> Padrão</>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="center">
        <div className="space-y-3">
          <div className="border-b pb-2">
            <div className="text-sm font-semibold">{provider.display_name || provider.name}</div>
            <div className="text-xs text-muted-foreground">
              canal <strong>{provider.channel_type}</strong> · plano <strong>{plan.name}</strong>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Habilitado</Label>
            <Switch checked={draftAvail} onCheckedChange={setDraftAvail} disabled={!canEdit || saving} />
          </div>

          <div className={cn("flex items-center justify-between", !draftAvail && "opacity-50")}>
            <Label className="text-xs">Provider padrão</Label>
            <Switch
              checked={draftDefault}
              onCheckedChange={setDraftDefault}
              disabled={!canEdit || saving || !draftAvail}
            />
          </div>
          <p className="text-[11px] text-muted-foreground leading-snug">
            Apenas um provider padrão por tipo de canal por plano.
          </p>

          {/* Elegibilidade à cota de instâncias do plano */}
          <div
            className={cn(
              "flex items-center justify-between",
              (!draftAvail || draftDefault) && "opacity-50",
            )}
          >
            <Label className="text-xs">Elegível à cota do plano</Label>
            <Switch
              checked={draftDefault ? true : draftPool}
              onCheckedChange={setDraftPool}
              disabled={!canEdit || saving || !draftAvail || draftDefault}
            />
          </div>
          <p className="text-[11px] text-muted-foreground leading-snug">
            Permite que o tenant escolha este provider ao criar a instância incluída
            no plano. O provider padrão já é elegível automaticamente.
          </p>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!canEdit || saving}>
              Salvar
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};


// ============= Página =============
export default function WlPlanMatrix() {
  const { canView, canEdit: canEditPerm } = useWlPermissions();
  const canView_ = canView("wl.plan_matrix");
  const canEdit = canEditPerm("wl.plan_matrix");

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(true);
  const [activeTab, setActiveTab] = useState<"channels" | "providers">("channels");

  const { data, isLoading } = useWlPlanMatrix({ search, includeInactive });
  const { upsertChannel, upsertProvider } = useWlPlanMatrixMutations();

  const plans = data?.plans ?? [];
  const channels = data?.channel_types ?? [];
  const providers = data?.providers ?? [];

  // Indexa as configs em Map para lookup O(1) na renderização da matriz.
  const channelConfigMap = useMemo(() => {
    const m = new Map<string, WlMatrixPlanChannel>();
    (data?.plan_channel_types ?? []).forEach((c) => {
      m.set(`${c.plan_id}::${c.channel_type_id}`, c);
    });
    return m;
  }, [data?.plan_channel_types]);

  const providerConfigMap = useMemo(() => {
    const m = new Map<string, WlMatrixPlanProvider>();
    (data?.plan_providers ?? []).forEach((c) => {
      m.set(`${c.plan_id}::${c.provider_id}`, c);
    });
    return m;
  }, [data?.plan_providers]);

  // KPIs agregados
  const kpis = useMemo(() => {
    const activeChannels = channels.filter((c) => c.is_active).length;
    const activeProviders = providers.filter((p) => p.is_active).length;
    const cellsConfigured =
      (data?.plan_channel_types?.filter((x) => x.is_available).length ?? 0) +
      (data?.plan_providers?.filter((x) => x.is_available).length ?? 0);
    return {
      plans: plans.length,
      channels: activeChannels,
      providers: activeProviders,
      configured: cellsConfigured,
    };
  }, [plans, channels, providers, data?.plan_channel_types, data?.plan_providers]);

  if (!canView_) {
    return (
      <ListPageLayout>
        <ListPageHeader title="Matriz de Planos" description="Sem permissão" />
        <ListPageContent>
          <p className="p-6 text-sm text-muted-foreground">
            Você não tem permissão para visualizar a matriz de planos.
          </p>
        </ListPageContent>
      </ListPageLayout>
    );
  }

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Matriz de Planos"
        description="Configure canais e provedores disponíveis por plano"
      />

      <ListPageKpis columns={4}>
        <KpiCard color="purple" icon={LayoutGrid} label="Planos" value={isLoading ? "…" : kpis.plans} />
        <KpiCard color="blue" icon={MessageSquare} label="Canais ativos" value={isLoading ? "…" : kpis.channels} />
        <KpiCard color="orange" icon={Plug} label="Provedores ativos" value={isLoading ? "…" : kpis.providers} />
        <KpiCard color="green" icon={Check} label="Células configuradas" value={isLoading ? "…" : kpis.configured} />
      </ListPageKpis>

      <ListPageFiltersBar isFetching={isLoading}>
        <div className="relative flex-1 min-w-[220px] sm:flex-none sm:w-[280px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
          <Input
            className={cn(LIST_FILTERS_INPUT_CLASS, "pl-7 sm:pl-8")}
            placeholder="Buscar canal ou provedor…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setSearch(searchInput)}
            onBlur={() => searchInput !== search && setSearch(searchInput)}
          />
        </div>
        <Button
          size="sm"
          variant={includeInactive ? "default" : "outline"}
          onClick={() => setIncludeInactive((v) => !v)}
          className="h-7 sm:h-8 text-xs gap-1.5"
        >
          <Power className="h-3.5 w-3.5" />
          {includeInactive ? "Incluindo inativos" : "Somente ativos"}
        </Button>
      </ListPageFiltersBar>

      <ListPageContent fullBleed>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="space-y-3">
          <div className="px-3 pt-3">
            <TabsList>
              <TabsTrigger value="channels" className="gap-2">
                <MessageSquare className="h-4 w-4" /> Canais
                <Badge variant="secondary" className="ml-1 text-[10px]">{channels.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="providers" className="gap-2">
                <Plug className="h-4 w-4" /> Provedores
                <Badge variant="secondary" className="ml-1 text-[10px]">{providers.length}</Badge>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* ---- TAB: Canais × Planos ---- */}
          <TabsContent value="channels" className="m-0">
            {isLoading ? (
              <p className="p-4 text-sm text-muted-foreground">Carregando…</p>
            ) : channels.length === 0 || plans.length === 0 ? (
              <p className="p-12 text-center text-sm text-muted-foreground">
                Nenhum canal ou plano para exibir.
              </p>
            ) : (
              <MatrixTable
                rowLabel="Canal"
                rows={channels.map((c) => ({
                  id: c.id,
                  name: c.display_name,
                  subtitle: c.slug,
                  inactive: !c.is_active,
                }))}
                plans={plans}
                renderCell={(row, plan) => (
                  <ChannelCell
                    plan={plan}
                    channel={channels.find((c) => c.id === row.id)!}
                    config={channelConfigMap.get(`${plan.id}::${row.id}`)}
                    canEdit={canEdit}
                    onSave={(input) => upsertChannel.mutateAsync(input)}
                  />
                )}
              />
            )}
          </TabsContent>

          {/* ---- TAB: Provedores × Planos ---- */}
          <TabsContent value="providers" className="m-0">
            {isLoading ? (
              <p className="p-4 text-sm text-muted-foreground">Carregando…</p>
            ) : providers.length === 0 || plans.length === 0 ? (
              <p className="p-12 text-center text-sm text-muted-foreground">
                Nenhum provedor ou plano para exibir.
              </p>
            ) : (
              <MatrixTable
                rowLabel="Provedor"
                rows={providers.map((p) => ({
                  id: p.id,
                  name: p.display_name || p.name,
                  subtitle: p.channel_type,

                  inactive: !p.is_active,
                }))}
                plans={plans}
                renderCell={(row, plan) => (
                  <ProviderCell
                    plan={plan}
                    provider={providers.find((p) => p.id === row.id)!}
                    config={providerConfigMap.get(`${plan.id}::${row.id}`)}
                    canEdit={canEdit}
                    onSave={(input) => upsertProvider.mutateAsync(input)}
                  />
                )}
              />
            )}
          </TabsContent>
        </Tabs>
      </ListPageContent>
    </ListPageLayout>
  );
}

// ============= Tabela Pivot reutilizável =============
interface MatrixRow {
  id: string;
  name: string;
  subtitle: string;
  inactive: boolean;
}

interface MatrixTableProps {
  rowLabel: string;
  rows: MatrixRow[];
  plans: WlMatrixPlan[];
  renderCell: (row: MatrixRow, plan: WlMatrixPlan) => React.ReactNode;
}

const MatrixTable = ({ rowLabel, rows, plans, renderCell }: MatrixTableProps) => (
  <div className="overflow-auto max-h-[calc(100vh-360px)]">
    <table className="w-full border-collapse text-xs">
      <thead className="sticky top-0 z-20 bg-background">
        <tr>
          <th className="sticky left-0 z-30 bg-background text-left px-3 py-2 border-b border-r min-w-[200px]">
            {rowLabel}
          </th>
          {plans.map((p) => (
            <th
              key={p.id}
              className={cn(
                "px-2 py-2 border-b text-center min-w-[140px] font-medium",
                !p.is_active && "text-muted-foreground/60",
              )}
            >
              <div className="flex flex-col items-center leading-tight">
                <span>{p.name}</span>
                {!p.is_active && (
                  <Badge variant="secondary" className="mt-0.5 text-[9px] py-0">inativo</Badge>
                )}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} className="hover:bg-muted/30">
            <td
              className={cn(
                "sticky left-0 z-10 bg-background px-3 py-2 border-b border-r align-middle",
                row.inactive && "text-muted-foreground/60",
              )}
            >
              <div className="font-medium">{row.name}</div>
              <div className="text-[10px] text-muted-foreground truncate max-w-[180px]">
                {row.subtitle}
              </div>
              {row.inactive && (
                <Badge variant="secondary" className="mt-0.5 text-[9px] py-0">inativo</Badge>
              )}
            </td>
            {plans.map((p) => (
              <td key={p.id} className="px-1.5 py-1.5 border-b align-middle">
                {renderCell(row, p)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
